import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';

import { BaseComponent } from '../../core';
import { LibCoreLayoutModule } from '../core-layout';
import { DateTimeUtils } from '../../utils';
import { DATE_FORMAT } from '../../models';

export interface PersonalDataFormData {
  name: string;
  surname: string;
  birthday: string;
}

@Component({
  selector: 'lib-personal-data-form',
  templateUrl: './personal-data-form.component.html',
  styleUrls: ['./personal-data-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    // Material design
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
})
export class PersonalDataFormComponent extends BaseComponent {
  @Input() set name(value: string | undefined) {
    if (value !== this.controlName.value) {
      this.controlName.setValue(value);
    }
  }

  @Input() set surname(value: string | undefined) {
    if (value !== this.controlSurname.value) {
      this.controlSurname.setValue(value);
    }
  }

  @Input() set birthday(value: string | undefined) {
    if (value !== this.controlBirthday.value) {
      this.controlBirthday.setValue(value);
    }
  }

  @Output() personalDataChanged = new EventEmitter<PersonalDataFormData>();

  public readonly FORM_NAME = {
    NAME: 'name',
    SURNAME: 'surname',
    BIRTHDAY: 'birthday',
  };

  public today = new Date();

  public submitForm: FormGroup;

  constructor() {
    super();

    this.submitForm = new FormGroup({
      [this.FORM_NAME.NAME]: new FormControl(null, [Validators.required]),
      [this.FORM_NAME.SURNAME]: new FormControl(null, [Validators.required]),
      [this.FORM_NAME.BIRTHDAY]: new FormControl(null),
    });

    this.submitForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((value: PersonalDataFormData) => {
        if (this.submitForm.valid) {
          this.personalDataChanged.emit(value);
        } else {
          this.personalDataChanged.emit(null);
        }
      });
  }

  public changeDatePicker(): void {
    if (this.controlBirthday.value) {
      this.controlBirthday.setValue(
        DateTimeUtils.formatDateToString(
          this.controlBirthday.value,
          DATE_FORMAT.DATE_FORMAT_BACKEND
        )
      );
    }
  }

  public get controlName(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.NAME);
  }

  public get controlSurname(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.SURNAME);
  }

  public get controlBirthday(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.BIRTHDAY);
  }
}
